import React, { useEffect, useState } from "react";
import { apiCall } from "utils/apiClient";

interface JobsTabProps {
  partnerId: number | string;
}

interface JobRow {
  id: number | string;
  jobNumber: string;
  jobTitle: string;
  status: string;
  partnerStatus: string;
  amount: number;
  assignedDate?: string;
  completedDate?: string;
  partnerIds: string[];
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeId = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const parseIdList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((id) => normalizeId(id))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((id) => normalizeId(id))
          .filter(Boolean);
      }
    } catch {
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((id) => normalizeId(id))
          .filter(Boolean);
      }
    }

    return [trimmed];
  }

  if (typeof value === "number" || typeof value === "bigint") {
    return [String(value)];
  }

  return [];
};

const getPartnerIds = (job: any): string[] => {
  const candidates = [
    ...parseIdList(job?.partner_ids),
    ...parseIdList(job?.partnerIds),
    ...parseIdList(job?.partner_id),
    ...parseIdList(job?.partnerId),
  ];

  return Array.from(new Set(candidates));
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

const formatStatus = (value?: string) => {
  if (!value) return "-";
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusBadgeClass = (status?: string) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
    case "assigned":
      return "bg-purple-100 text-purple-700";
    case "in-progress":
      return "bg-blue-100 text-blue-700";
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const getRowsFromResponse = (response: any): JobRow[] => {
  const sourceData = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.jobs || response?.jobs || [];

  if (!Array.isArray(sourceData)) return [];

  return sourceData.map((item: any) => ({
    id: item?.id ?? item?.job_id ?? item?.job_number,
    jobNumber: String(
      item?.job_id ?? item?.job_number ?? item?.id ?? "-"
    ),
    jobTitle: String(item?.title ?? item?.job_title ?? "-"),
    status: String(item?.status ?? "pending"),
    partnerStatus: String(item?.partner_status ?? "not-assigned"),
    amount: toNumber(
      item?.total_partner_amount ?? item?.totalPartnerAmount ?? item?.amount
    ),
    assignedDate: item?.assigned_date ?? item?.estimated_start_date ?? item?.scheduled_date,
    completedDate: item?.completed_date ?? item?.completedDate,
    partnerIds: getPartnerIds(item),
  }));
};

const JobsTab: React.FC<JobsTabProps> = ({ partnerId }) => {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const partnerKey = normalizeId(partnerId);

    if (!partnerKey) {
      setJobs([]);
      setLoading(false);
      setError("Partner id is missing.");
      return;
    }

    let cancelled = false;

    const fetchPartnerJobs = async () => {
      setLoading(true);
      setError(null);

      try {
        const attempts = [
          {
            assumeScoped: true,
            request: () =>
              apiCall({
                endpoint: `partner-jobs/partner/${partnerKey}`,
                method: "GET",
              }),
          },
          {
            assumeScoped: true,
            request: () =>
              apiCall({
                endpoint: "partner-jobs",
                method: "GET",
                params: {
                  partner_id: partnerKey,
                  page: "1",
                  limit: "200",
                },
              }),
          },
          {
            assumeScoped: false,
            request: () =>
              apiCall({
                endpoint: "partner-jobs",
                method: "GET",
                params: {
                  page: "1",
                  limit: "200",
                },
              }),
          },
        ];

        let resolvedRows: JobRow[] = [];

        for (const attempt of attempts) {
          const response = await attempt.request();
          if (response?.status !== 200 && response?.status !== 201) {
            continue;
          }

          const rows = getRowsFromResponse(response);
          const filtered = rows.filter((row) => row.partnerIds.includes(partnerKey));

          if (filtered.length > 0) {
            resolvedRows = filtered;
            break;
          }

          if (attempt.assumeScoped && rows.length > 0) {
            resolvedRows = rows;
            break;
          }
        }

        if (!cancelled) {
          setJobs(resolvedRows);
        }
      } catch (err: any) {
        if (!cancelled) {
          setJobs([]);
          setError(err?.message || "Failed to load jobs");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPartnerJobs();

    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  if (loading) {
    return <div className="text-gray-500">Loading jobs...</div>;
  }

  if (error) {
    return <div className="text-red-600 text-sm">{error}</div>;
  }

  if (jobs.length === 0) {
    return (
      <div className="text-gray-500">
        No jobs assigned to this partner.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="px-4 py-2 text-left">Job No</th>
            <th className="px-4 py-2 text-left">Job Title</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Partner Status</th>
            <th className="px-4 py-2 text-left">Amount</th>
            <th className="px-4 py-2 text-left">Scheduled</th>
            <th className="px-4 py-2 text-left">Completed</th>
          </tr>
        </thead>

        <tbody>
          {jobs.map((job) => (
            <tr key={job.id} className="border-b">
              <td className="px-4 py-2 font-medium">{job.jobNumber}</td>
              <td className="px-4 py-2">{job.jobTitle}</td>
              <td className="px-4 py-2">
                <span
                  className={`capitalize text-xs font-semibold px-2 py-1 rounded-full ${getStatusBadgeClass(
                    job.status
                  )}`}
                >
                  {formatStatus(job.status)}
                </span>
              </td>
              <td className="px-4 py-2">{formatStatus(job.partnerStatus)}</td>
              <td className="px-4 py-2 font-semibold">
                {"\u20B9"}
                {job.amount.toLocaleString("en-IN")}
              </td>
              <td className="px-4 py-2 text-gray-600">{formatDate(job.assignedDate)}</td>
              <td className="px-4 py-2 text-gray-600">{formatDate(job.completedDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default JobsTab;
